import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	Button,
	Form,
	Message,
	Modal
} from 'semantic-ui-react';

import useForm from '../../../../../hooks/useForm';

import {
	updateCredential,
	createCredential
} from '../../../../../api/nora/contacts';

import { useContactState } from '../../../../../contexts/ContactsContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import FormDatePicker from '../../../../Form/FormDatePicker';
import FormInput from '../../../../Form/FormInput';
import FormSelect from '../../../../Form/FormSelect';
import FormTextArea from '../../../../Form/FormTextArea';

import { log } from '../../../../../services/utils';
import FormErrors from '../../../../Shared/FormErrors';

const CredentialsModal = ( {
	closeModal,
	credential,
	open,
	retrieveCredentialsList
} ) => {
	const {
		contact,
		credentialTypes
	} = useContactState();
	const { setApiErrorAlert } = useUIAction();

	const [ forceValidation, setForceValidation ] = useState( false );
	const [ isSaving, setIsSaving ] = useState( false );
	const [ isClosing, setIsClosing ] = useState( false );

	const {
		formData,
		updateField,
		reinitializeFormData,
		setFormData
	} = useForm( {} );

	useEffect( () => {
		if ( credential ) {
			setFormData( {
				...credential,
				type: credential.type.id
			} );
		}
	}, [
		credential,
		setFormData
	] );

	const errors = useMemo( () => ( {
		label: [
			{
				error: !formData.label,
				message: 'Label is a required field.',
				fieldLabel: 'Label'
			}
		],
		type: [
			{
				error: !formData.type,
				message: 'Credential Type is a required field.',
				fieldLabel: 'Credential Type'
			}
		],
		completionDate: [
			// {
			// 	error: !formData.completionDate,
			// 	message: 'Completion Date is a required field.',
			// 	fieldLabel: 'Completion Date'
			// },
			{
				error: formData.completionDate && formData.expirationDate && new Date( formData.expirationDate ) <= new Date( formData.completionDate ),
				message: 'Completion Date must procede Expiration Date by at least one calendar day.',
				fieldLabel: 'Expiration Date'
			}
		],
		expirationDate: [
			// {
			// 	error: !formData.expirationDate,
			// 	message: 'Expiration Date is a required field.',
			// 	fieldLabel: 'Expiration Date'
			// },
			{
				error: formData.completionDate && formData.expirationDate && new Date( formData.expirationDate ) <= new Date( formData.completionDate ),
				message: 'Expiration Date must suceed Completion Date by at least one calendar day.',
				fieldLabel: 'Expiration Date'
			}
		]
	} ), [
		formData.label,
		formData.type,
		formData.completionDate,
		formData.expirationDate
	] );

	const validate = useCallback( () => Object.values( errors ).some( errs => errs.some( err => err.error ) ), [ errors ] );

	const mapFormErrors = useCallback(
		() => Object.values( errors ).map( errors => errors.map( error => error.error
			? <Message.Item key={ error.fieldLabel }>{ error.fieldLabel }</Message.Item>
			: null ) ),
		[ errors ]
	);

	const handleCloseModal = useCallback( async () => {
		setIsClosing( true );
		await retrieveCredentialsList();
		closeModal();
		setIsClosing( false );
	}, [
		closeModal,
		retrieveCredentialsList,
		setIsClosing
	] );

	const handleSave = useCallback( async () => {
		if ( validate() ) {
			if ( !forceValidation ) {
				setForceValidation( true );
			}
		} else {
			setIsSaving( true );

			setForceValidation( false );
			let {
				type,
				...rest
			} = formData;
			const data = {
				...rest,
				type: { id: type }
			};

			try {
				if ( credential?.id === 0 ) {
					await createCredential( contact?.id, data );
				} else {
					await updateCredential( contact?.id, data );
					reinitializeFormData();
				}
			} catch ( error ) {
				setApiErrorAlert( error );
			}

			await retrieveCredentialsList();
			closeModal();
			setIsSaving( false );
		}
	}, [
		closeModal,
		forceValidation,
		formData,
		credential,
		contact,
		reinitializeFormData,
		retrieveCredentialsList,
		setApiErrorAlert,
		setIsSaving,
		validate
	] );

	if ( !credential ) {
		return null;
	}

	return (
		<Modal open={ open }>
			{ log( 'credentialsModal' ) }
			<Modal.Header>
				{ contact?.id === 0
					? 'Add Credential'
					: 'Edit Credential'
				}
			</Modal.Header>
			<Modal.Content>
				<div>
					<Form
						autoComplete="off"
						error={ forceValidation && validate()}
					>
						<input
							className="hidden-input"
							name="name"
						/>

						<Form.Group>
							<FormInput
								errors={ errors.label }
								fieldLabel="Label"
								forceValidation={ forceValidation }
								maxLength={ 125 }
								name="label"
								onChange={ updateField }
								required
								value={ formData.label || '' }
								width={ 10 }
							/>
						</Form.Group>

						<Form.Group>
							<FormSelect
								errors={ errors.type }
								fieldLabel="Type"
								forceValidation={ forceValidation }
								name="type"
								onChange={ updateField }
								options={ credentialTypes }
								required
								search
								value={ formData.type || '' }
								width={ 4 }
							/>
							<FormDatePicker
								errors={ errors.completionDate }
								fieldLabel="Completion Date"
								forceValidation={ forceValidation }
								maxLength={ 255 }
								name="completionDate"
								onChange={ updateField }
								value={ formData.completionDate || '' }
								width={ 3 }
							/>
							<FormDatePicker
								errors={ errors.expirationDate }
								fieldLabel="Expiration Date"
								forceValidation={ forceValidation }
								maxLength={ 255 }
								name="expirationDate"
								onChange={ updateField }
								value={ formData.expirationDate || '' }
								width={ 3 }
							/>
						</Form.Group>

						<Form.Group>
							<FormTextArea
								fieldLabel="Description"
								name="description"
								onChange={ updateField }
								rows={ 6 }
								value={ formData.description || '' }
								width={ 16 }
							/>
						</Form.Group>
						<FormErrors mapFormErrors={ mapFormErrors } />
					</Form>
				</div>
			</Modal.Content>
			<Modal.Actions>
				<Button
					content={ credential.id === 0
						? 'Save and Add'
						: 'Save and Close'
					}
					loading={ isSaving }
					onClick={ handleSave }
				/>
				<Button
					content="Cancel"
					loading={ isClosing }
					onClick={ handleCloseModal }
				/>
			</Modal.Actions>
		</Modal>
	);
};

CredentialsModal.propTypes = {
	closeModal: PropTypes.func.isRequired,
	credential: PropTypes.object,
	open: PropTypes.bool,
	retrieveCredentialsList: PropTypes.func.isRequired
};

export default memo( CredentialsModal );
